<template>
  <div id="view-board" class="view-board">
    <h2 class="page-heading sr-only">View Board</h2>
    <h3 class="board-details__name">{{ name }}</h3>
    <div class="board-details">
      <div class="link-wrapper">
        <router-link
          v-if="boardId"
          class="link link--play"
          :class="`link--${hasEnoughTilesToBePlayable ? 'enabled' : 'disabled'}`"
          :to="{name: 'play', params: {boardIdOrSlug: slug}}"
          title="Play"
          :tabindex="hasEnoughTilesToBePlayable ? '0' : '-1'"
          ><span class="sr-only">Play this board</span><span class="material-icons" aria-hidden="true">play_arrow</span></router-link
        ><router-link v-if="boardId" class="link link--edit" :to="{name: 'edit-board', params: {boardId: boardId}}" title="Edit"
          ><span class="sr-only">Edit this board</span><span class="material-icons" aria-hidden="true">edit</span></router-link
        >
      </div>
      <div class="board-details__rows-wrapper">
        <div class="board-details__row">
          <h4 class="board-details__property">Board ID</h4>
          <p class="board-details__value">{{ boardId }}</p>
        </div>
        <div class="board-details__row">
          <h4 class="board-details__property">Slug</h4>
          <p class="board-details__value">{{ slug }}</p>
        </div>
        <div class="board-details__row">
          <h4 class="board-details__property">Prompt prefix</h4>
          <p class="board-details__value">{{ promptPrefix }}</p>
        </div>
        <div class="board-details__row">
          <h4 class="board-details__property">Category</h4>
          <p class="board-details__value">{{ categoryFormatted }}</p>
        </div>
        <div class="board-details__row">
          <h4 class="board-details__property">Mode</h4>
          <p class="board-details__value">{{ modeFormatted }}<span class="mode-indicator" :class="modeModifierClass"></span></p>
        </div>
        <div class="board-details__row">
          <h4 class="board-details__property">Folder name</h4>
          <p class="board-details__value">{{ imagesFolderName }}</p>
        </div>
        <div class="board-details__row">
          <h4 class="board-details__property">Keywords</h4>
          <p class="board-details__value">{{ keywords.join(', ') }}</p>
        </div>
        <div class="board-details__row">
          <h4 class="board-details__property">Portrait layout</h4>
          <p class="board-details__value">{{ boardPortraitLayout }}</p>
        </div>
        <div class="board-details__row">
          <h4 class="board-details__property">Num tiles (total)</h4>
          <p class="board-details__value">{{ numTilesTotal }}</p>
        </div>
        <div class="board-details__row">
          <h4 class="board-details__property">Num tiles (disabled)</h4>
          <p class="board-details__value">{{ numTilesDisabled }}</p>
        </div>
        <div class="board-details__row">
          <h4 class="board-details__property">Num tiles (enabled)</h4>
          <p class="board-details__value">
            {{ numTilesEnabled }}
            <span v-if="!hasEnoughTilesToBePlayable" class="not-enough-tiles-warning">WARNING: Not enough tiles enabled to fill the board !</span>
          </p>
        </div>
        <div class="board-details__row">
          <h4 class="board-details__property">Board size</h4>
          <p class="board-details__value">{{ boardSize }}</p>
        </div>
        <div class="board-details__row">
          <h4 class="board-details__property">Tile background size</h4>
          <p class="board-details__value">{{ tileBackgroundSizeFormatted }}</p>
        </div>
        <div class="board-details__row">
          <h4 class="board-details__property">Tile background position</h4>
          <p class="board-details__value">{{ tileBackgroundPositionFormatted }}</p>
        </div>
        <div class="board-details__row">
          <h4 class="board-details__property">Tile padding</h4>
          <p class="board-details__value">{{ tilePadding ? '✔' : '✘' }}</p>
        </div>
        <div class="board-details__row">
          <h4 class="board-details__property">Invert tile images in Dark Mode</h4>
          <p class="board-details__value">{{ invertTileImagesInDarkMode ? '✔' : '✘' }}</p>
        </div>
        <div class="board-details__row">
          <h4 class="board-details__property">Tile background colour</h4>
          <p class="board-details__value">{{ tileBackgroundColor }}<span v-if="tileBackgroundColor" class="tile-background-color-chip" :style="colorChipInlineStyle"></span></p>
        </div>
        <div class="board-details__row">
          <h4 class="board-details__property">Tile radial gradient hex colour</h4>
          <p class="board-details__value">
            {{ tileRadialGradientHexColor }}<span v-if="tileRadialGradientHexColor" class="tile-radial-gradient-color-chip" :style="colorChipRadialGradientInlineStyle"></span>
          </p>
        </div>
        <div class="board-details__row">
          <h4 class="board-details__property">Promoted</h4>
          <p class="board-details__value">{{ promoted ? '✔' : '✘' }}</p>
        </div>
        <div class="board-details__row">
          <h4 class="board-details__property">Created at</h4>
          <p class="board-details__value">
            {{ createdAtFormatted }} <span class="time-ago">({{ createdAtTimeAgo }})</span>
          </p>
        </div>
      </div>
    </div>
    <div class="form-row form-row--preview-with-backgrounds">
      <label class="control control--checkbox"
        >Preview tiles with backgrounds showing
        <input type="checkbox" v-model="previewWithBackgrounds" />
        <div class="control__indicator"></div>
      </label>
    </div>
    <ul class="tile-list style-none">
      <li v-for="(tile, index) in tiles" :key="tile.tileId" class="tile-list__item">
        <ViewBoardTile
          :answerText="tile.answerText"
          :backgroundColor="tile.backgroundColor"
          :enabled="tile.enabled"
          :imageFilename="tile.imageFilename"
          :imagesFolderName="imagesFolderName"
          :index="index"
          :invertTileImagesInDarkMode="invertTileImagesInDarkMode"
          :previewWithBackgrounds="previewWithBackgrounds"
          :prompt="tile.prompt"
          :radialGradientHexColor="tile.radialGradientHexColor"
          :tileBackgroundColor="tileBackgroundColor"
          :tileId="tile.tileId"
          :tileIdCopiedToClipboard="tileIdCopiedToClipboard"
          :tilePadding="tilePadding"
          :tileRadialGradientHexColor="tileRadialGradientHexColor"
          @copied-to-clipboard="copiedToClipboard"
        />
      </li>
    </ul>
    <p v-if="numTilesWithNoPromptOrId > 0">
      <strong>Plus another {{ numTilesWithNoPromptOrId }} tiles that don&rsquo;t have a 'prompt' or 'tileId'</strong>
    </p>
    <a href="#top" class="scroll-to-top" v-smooth-scroll="{duration: 50, offset: 0, updateHistory: false}"
      ><span class="sr-only">Scroll to top</span><span class="material-icons" aria-hidden="true">keyboard_arrow_up</span></a
    >
  </div>
</template>

<script>
import {createNamespacedHelpers} from 'vuex';
import router from './../router/index';
import firebase from 'firebase/app';
import db from './../firebaseInit';
import {CATEGORIES, PATH_TO_TILE_IMAGES, BOARD_MODES, TILE_BACKGROUND_POSITIONS, TILE_BACKGROUND_SIZES} from './../constants/globalConstants';
import getBoardSize from './../helpers/getBoardSize';
import ViewBoardTile from './../components/ViewBoardTile';
import formatDateFromFirebaseTimestamp from './../helpers/formatDateFromFirebaseTimestamp';
import timeAgoFromFirebaseTimestamp from './../helpers/timeAgoFromFirebaseTimestamp';
import hexToHsl from './../helpers/hexToHsl';
import getRadialGradientCss from './../helpers/getRadialGradientCss';

const userVuexModule = createNamespacedHelpers('userVuexModule');

export default {
  name: 'view-board',
  title: 'View Board',
  components: {
    ViewBoardTile,
  },
  data() {
    return {
      boardCreatedAt: null,
      boardId: null,
      boardPortraitLayout: null,
      category: null,
      categoryFormatted: null,
      colorChipInlineStyle: '',
      colorChipRadialGradientInlineStyle: '',
      createdAt: null,
      createdAtFormatted: null,
      createdAtTimeAgo: null,
      hasEnoughTilesToBePlayable: false,
      imagesFolderName: null,
      invertTileImagesInDarkMode: null,
      keywords: [],
      mode: null,
      modeFormatted: null,
      modeModifierClass: '',
      name: null,
      previewWithBackgrounds: false,
      promoted: null,
      promptPrefix: null,
      slug: '',
      temp: 'foobar',
      tileBackgroundColor: null,
      tileBackgroundPosition: null,
      tileBackgroundPositionFormatted: null,
      tileBackgroundSize: null,
      tileBackgroundSizeFormatted: null,
      tileIdCopiedToClipboard: '',
      tilePadding: null,
      tileRadialGradientHexColor: null,
      tiles: [],
      tilesWithNoPromptOrId: [],
    };
  },
  beforeRouteEnter(to, from, next) {
    db.collection('boards')
      .where('boardId', '==', to.params.boardId)
      .get()
      .then(snapshot => {
        if (snapshot.size >= 1) {
          snapshot.forEach(doc => {
            next(vm => {
              vm.boardCreatedAt = doc.data().boardCreatedAt;
              vm.boardId = doc.data().boardId;
              vm.boardPortraitLayout = doc.data().boardPortraitLayout;
              vm.category = doc.data().category;
              vm.createdAt = doc.data().boardCreatedAt;
              vm.imagesFolderName = doc.data().imagesFolderName;
              vm.invertTileImagesInDarkMode = doc.data().invertTileImagesInDarkMode;
              vm.keywords = doc.data().keywords;
              vm.mode = doc.data().mode;
              vm.name = doc.data().name;
              vm.promoted = doc.data().promoted;
              vm.promptPrefix = doc.data().promptPrefix;
              vm.slug = doc.data().slug;
              vm.tileBackgroundColor = doc.data().tileBackgroundColor;
              vm.tileBackgroundPosition = doc.data().tileBackgroundPosition;
              vm.tileBackgroundSize = doc.data().tileBackgroundSize;
              vm.tilePadding = doc.data().tilePadding;
              vm.tileRadialGradientHexColor = doc.data().tileRadialGradientHexColor;
              db.collection('boards')
                .doc(vm.boardId)
                .collection('tiles')
                .get()
                .then(snapshot => {
                  snapshot.docs.forEach(doc => {
                    if (doc.data().prompt !== undefined && doc.data().prompt !== '' && doc.data().tileId !== undefined && doc.data().tileId !== '') {
                      vm._data.tiles.push(doc.data());
                    } else {
                      vm._data.tilesWithNoPromptOrId.push(doc.data());
                    }
                  });
                  vm.tilesLoaded();
                });
            });
          });
        } else {
          router.replace({name: 'boards'});
        }
      });
  },
  created() {
    window.scrollTo(0, 0);
  },
  watch: {
    category: function() {
      this.categoryFormatted = CATEGORIES[this.category].DISPLAY_NAME || '';
    },
    mode: function() {
      this.modeFormatted = BOARD_MODES[this.mode].DISPLAY_NAME || '';
      this.modeModifierClass = `mode-indicator--${BOARD_MODES[this.mode].CSS_MODIFIER}` || '';
    },
    createdAt: function() {
      this.createdAtFormatted = formatDateFromFirebaseTimestamp(this.createdAt);
      this.createdAtTimeAgo = timeAgoFromFirebaseTimestamp(this.createdAt);
    },
    tileBackgroundPosition: function() {
      this.tileBackgroundPositionFormatted = TILE_BACKGROUND_POSITIONS[this.tileBackgroundPosition].DISPLAY_NAME || '';
    },
    tileBackgroundSize: function() {
      this.tileBackgroundSizeFormatted = TILE_BACKGROUND_SIZES[this.tileBackgroundSize].DISPLAY_NAME || '';
    },
    tileBackgroundColor: function() {
      if (this.tileBackgroundColor) {
        this.colorChipInlineStyle = `background-color: ${this.tileBackgroundColor};`;
      } else {
        this.colorChipInlineStyle = '';
      }
    },
    tileRadialGradientHexColor: function() {
      if (this.tileRadialGradientHexColor) {
        this.colorChipRadialGradientInlineStyle = `background-color: ${this.tileRadialGradientHexColor};`;
      } else {
        this.colorChipRadialGradientInlineStyle = '';
      }
    },
  },
  methods: {
    tilesLoaded() {
      this.hasEnoughTilesToBePlayable = this.numTilesEnabled >= this.boardSize;
    },
    copiedToClipboard(tileId) {
      this.tileIdCopiedToClipboard = tileId;
    },
  },
  computed: {
    ...userVuexModule.mapGetters(['userCanRead', 'userCanEdit', 'userCanCreate', 'userCanDelete']),
    getPathToTileImages: function() {
      return PATH_TO_TILE_IMAGES;
    },
    numTilesTotal: function() {
      return this.tiles.length;
    },
    numTilesDisabled: function() {
      return this.tiles.filter(tile => !tile.enabled).length;
    },
    numTilesEnabled: function() {
      return this.tiles.filter(tile => tile.enabled).length;
    },
    boardSize: function() {
      return getBoardSize(this.boardPortraitLayout);
    },
    numTilesWithNoPromptOrId: function() {
      return this.tilesWithNoPromptOrId.length;
    },
  },
};
</script>

<style lang="scss" scoped>
.tile-item {
  display: flex;
}

.tile-details {
  &--disabled {
    opacity: 0.3;
  }
}

.board-details {
  background-color: var(--color-form-element-background);
  border-radius: var(--border-radius);
  font-size: 1.4rem;
  margin: 3rem auto 3rem auto;
  max-width: var(--max-width-main-content);
  padding: var(--padding-l) var(--padding-s) var(--padding-s) var(--padding-s);
  position: relative;

  .link-wrapper {
    align-items: center;
    display: flex;
    position: absolute;
    right: 1rem;
    top: -2.6rem;
    background-color: var(--color-view-board-link-wrapper-bg);
    border-radius: var(--border-radius);
    padding: 0 0.7rem;
    border: 1px solid var(--color-view-board-link-wrapper-border);
  }

  .link {
    &--disabled {
      pointer-events: none;
      color: var(--text-color-alpha-33);
    }

    &--play {
      display: inline-block;
      margin-right: 1rem;

      .material-icons {
        font-size: 5rem;
      }
    }

    &--edit {
      display: inline-block;
      margin-right: 1rem;

      .material-icons {
        font-size: 3.8rem;
      }
    }
  }

  &__name {
    font-size: 3.5rem;
    margin-top: 0;
    text-align: center;
  }

  &__rows-wrapper {
    > .board-details__row {
      &:nth-child(odd) {
        background-color: var(--color-view-board-details-row-odd);
      }
      &:nth-child(even) {
        background-color: var(--color-view-board-details-row-even);
      }
    }
  }

  &__row {
    display: flex;
    padding: 0.2rem;
  }

  &__property {
    font-size: 1.4rem;
    line-height: 1.5;
    margin: 0;
    width: 50%;

    @include breakpoint($mq-sm-md) {
      width: 40%;
    }

    @include breakpoint($mq-md) {
      margin: 0 1rem 0 0;
      width: 30%;
    }
  }

  &__value {
    line-height: 1.5;
    margin: 0;
    width: 50%;

    @include breakpoint($mq-sm-md) {
      width: 60%;
    }

    @include breakpoint($mq-md) {
      width: 70%;
    }
  }
}

.tile-list {
  &__item {
    margin-bottom: 2rem;
  }
}

.mode-indicator {
  border-radius: 50%;
  display: inline-block;
  height: 0.5rem;
  margin: 0 0 0 0.5rem;
  transform: translateY(-1px);
  vertical-align: middle;
  width: 0.5rem;

  &--live {
    background-color: green;
  }

  &--testing {
    background-color: orange;
  }

  &--disabled {
    background-color: red;
  }
}

.time-ago {
  opacity: 0.4;
}

.tile-background-color-chip,
.tile-radial-gradient-color-chip {
  display: inline-block;
  height: 1em;
  margin: 0 0 0 0.5rem;
  transform: translateY(-1px);
  vertical-align: middle;
  width: 1em;
}

.scroll-to-top {
  align-items: center;
  display: flex;
  justify-content: center;
  line-height: 1;
  margin: 0 auto;
  text-align: center;
  text-decoration: none;
  width: 4rem;

  .material-icons {
    font-size: 4rem;
  }
}

.not-enough-tiles-warning {
  color: red;
  font-weight: 500;
  margin-left: 1rem;
}

.form-row--preview-with-backgrounds {
  margin: 3rem auto 3rem auto;
  max-width: var(--max-width-main-content);
  padding: 0 var(--padding-s);

  label {
    display: inline-block;
  }
}
</style>
